import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useProducts } from '../useProducts';
import { useSiteSettings } from '../SiteSettingsContext';
import { getCustomJewelryConfig, isCustomJewelryLinkVisible } from '../jewelry/customJewelrySettings';
import { formatDisplayTitle } from '../formatProductTitle';
import ProductListingToolbar from '../catalog/ProductListingToolbar';
import CatalogPagination from '../catalog/CatalogPagination';
import CatalogGridSkeleton from '../catalog/CatalogGridSkeleton';
import ProductCard from '../components/ProductCard';
import {
  buildCollectionOptions,
  buildGroupOptions,
  buildRecipientOptions,
  filterGiftFinderProducts,
  giftFinderSummary,
  GIFT_BUDGET_OPTIONS,
  getGiftFinderSteps,
  isLegacyGiftFinderSlug,
  parseGiftFinderCategory,
  parseGiftFinderRecipient,
  parseGiftFinderStep,
  productCountByCategory,
  productsForCollection,
} from './giftFinderConfig';
import { GIFT_FINDER_DEFAULTS } from '../content/giftFinderDefaults';
import { resolveRecipient, widenGiftFinderFilters } from './giftFinderGroups';
import {
  trackGiftFinderProductClick,
  trackGiftFinderResults,
  trackGiftFinderSelect,
  trackGiftFinderStepView,
  trackGiftFinderWiden,
} from './giftFinderAnalytics';
import './gift-finder.css';

const PAGE_SIZE = 12;

function OptionCard({ option, selected, onSelect }) {
  const showIcon = option.icon && option.icon !== '·';
  return (
    <button
      type="button"
      className={`gift-finder-option${selected ? ' gift-finder-option--selected' : ''}`}
      onClick={() => onSelect(option.id)}
      aria-pressed={selected}
    >
      {showIcon ? (
        <span className="gift-finder-option__icon" aria-hidden="true">
          {option.icon}
        </span>
      ) : null}
      <span className="gift-finder-option__label">{option.label}</span>
      <span className="gift-finder-option__hint">{option.hint}</span>
    </button>
  );
}

export default function GiftFinderPage({ config: configProp }) {
  const config = configProp || GIFT_FINDER_DEFAULTS;
  const wizardSteps = useMemo(() => getGiftFinderSteps(config), [config]);

  const { settings } = useSiteSettings();
  const customJewelry = getCustomJewelryConfig(settings?.store_copy);
  const showCustomJewelryPromo = isCustomJewelryLinkVisible(settings?.store_copy, 'gift_finder');

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { allProducts, categories, loading, error } = useProducts();

  const step = parseGiftFinderStep(searchParams.get('step'), config);
  const recipient = parseGiftFinderRecipient(searchParams);
  const category = parseGiftFinderCategory(searchParams, config);
  const groupRaw = String(searchParams.get('group') || 'any').trim().toLowerCase() || 'any';
  const group = isLegacyGiftFinderSlug(groupRaw, config) ? 'any' : groupRaw;
  const budget = String(searchParams.get('budget') || 'any').trim().toLowerCase() || 'any';

  const analyticsFilters = useMemo(
    () => ({ recipient, category, group, budget }),
    [recipient, category, group, budget]
  );

  const recipientStepEnabled =
    config.show_recipient_step && (config.recipients || []).length > 0 && wizardSteps.includes('recipient');

  const [sort, setSort] = useState('price-asc');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [page, setPage] = useState(1);

  const countByCategory = useMemo(() => productCountByCategory(allProducts), [allProducts]);

  const collectionOptions = useMemo(
    () => buildCollectionOptions(categories, countByCategory),
    [categories, countByCategory]
  );

  const scopedProducts = useMemo(
    () => productsForCollection(allProducts, category),
    [allProducts, category]
  );

  const categoryMeta = useMemo(() => {
    if (category === 'any') return null;
    return categories.find((c) => String(c.slug || '').trim().toLowerCase() === category) || null;
  }, [categories, category]);

  const recipientOptions = useMemo(
    () => buildRecipientOptions(config.recipients),
    [config.recipients]
  );

  const groupOptions = useMemo(
    () => buildGroupOptions(scopedProducts, { categoryMeta, categories, config }),
    [scopedProducts, categoryMeta, categories, config]
  );

  const taxonomyGroupCount = useMemo(() => {
    if (!categoryMeta) return 0;
    const subs = (categoryMeta.subcategories || []).filter((s) => s.is_visible !== false && s.slug !== 'all');
    const types = (categoryMeta.types || []).filter((t) => t.is_visible !== false);
    return subs.length + types.length;
  }, [categoryMeta]);

  const optionSets = useMemo(
    () => ({ collectionOptions, groupOptions, recipientOptions }),
    [collectionOptions, groupOptions, recipientOptions]
  );

  const validCategoryIds = useMemo(
    () => new Set(collectionOptions.map((o) => o.id)),
    [collectionOptions]
  );

  const validGroupIds = useMemo(() => new Set(groupOptions.map((o) => o.id)), [groupOptions]);

  const groupStepEnabled = config.show_group_step !== false && wizardSteps.includes('group');
  const budgetStepEnabled = config.show_budget_step !== false && wizardSteps.includes('budget');
  const showGroupStep = groupStepEnabled && groupOptions.length > 1;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, page]);

  useEffect(() => {
    setPage(1);
  }, [category, group, budget, recipient, inStockOnly, sort]);

  useEffect(() => {
    if (loading) return;
    if (category !== 'any' && !validCategoryIds.has(category)) {
      const next = new URLSearchParams(searchParams);
      next.set('category', 'any');
      setSearchParams(next, { replace: true });
    }
  }, [loading, category, validCategoryIds, searchParams, setSearchParams]);

  useEffect(() => {
    if (loading) return;
    const groupParam = String(searchParams.get('group') || 'any').trim().toLowerCase() || 'any';
    const legacyGroup = isLegacyGiftFinderSlug(groupParam, config);
    const invalidGroup = group !== 'any' && !validGroupIds.has(group);
    if (!legacyGroup && !(invalidGroup && step !== 'results')) return;
    const next = new URLSearchParams(searchParams);
    next.set('group', 'any');
    setSearchParams(next, { replace: true });
  }, [loading, step, group, validGroupIds, searchParams, setSearchParams]);

  const patchParams = (updates, { trackStep, trackValue } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') next.delete(k);
      else next.set(k, String(v));
    });
    if (updates.category !== undefined && updates.group === undefined) {
      next.set('group', 'any');
    }
    setSearchParams(next, { replace: false });
    if (trackStep && trackValue !== undefined) {
      const merged = { ...analyticsFilters, ...updates };
      trackGiftFinderSelect(trackStep, trackValue, merged);
    }
  };

  const goStep = (nextStep, extra = {}) => {
    patchParams({ step: nextStep, ...extra });
  };

  useEffect(() => {
    if (loading) return;
    if (step === 'group' && (!groupStepEnabled || !showGroupStep)) {
      if (budgetStepEnabled) goStep('budget', { group: 'any' });
      else goStep('results', { group: 'any', budget: 'any' });
    } else if (step === 'budget' && !budgetStepEnabled) {
      goStep('results', { budget: 'any' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- redirect disabled wizard steps
  }, [loading, step, showGroupStep, groupStepEnabled, budgetStepEnabled]);

  const stepIndex = wizardSteps.indexOf(step);
  const progressPct =
    step === 'results' ? 100 : Math.round(((stepIndex + 1) / wizardSteps.length) * 100);

  const [relaxedNotice, setRelaxedNotice] = useState(null);
  const widenAttemptedRef = useRef(false);
  const resultsTrackedRef = useRef(false);

  const catalogEmpty = !loading && collectionOptions.length <= 1;

  const results = useMemo(() => {
    if (step !== 'results') return [];
    return filterGiftFinderProducts(allProducts, {
      category,
      group,
      budget,
      recipient,
      inStockOnly,
      sort,
      config,
    });
  }, [allProducts, category, group, budget, recipient, inStockOnly, sort, step, config]);

  useEffect(() => {
    if (loading || catalogEmpty) return;
    trackGiftFinderStepView(step, analyticsFilters);
  }, [step, loading, catalogEmpty, analyticsFilters]);

  useEffect(() => {
    if (step !== 'results' || loading) {
      resultsTrackedRef.current = false;
      return;
    }
    if (resultsTrackedRef.current) return;
    resultsTrackedRef.current = true;
    trackGiftFinderResults(analyticsFilters, results.length);
  }, [step, loading, results.length, analyticsFilters]);

  useEffect(() => {
    if (step !== 'results') {
      widenAttemptedRef.current = false;
      setRelaxedNotice(null);
      return;
    }
    if (!config.auto_widen_results || loading || results.length >= 3 || widenAttemptedRef.current) {
      return;
    }
    const widen = widenGiftFinderFilters({ category, group, budget, recipient }, config);
    if (!widen) return;
    widenAttemptedRef.current = true;
    setRelaxedNotice(widen.relaxedLabel);
    trackGiftFinderWiden(widen.relaxedLabel, analyticsFilters);
    patchParams({ ...widen.filters, step: 'results' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-widen once per results visit
  }, [step, results.length, loading, config.auto_widen_results, category, group, budget, recipient]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const paginated = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const categoryName = (slug) => {
    const c = categories.find((x) => x.slug === slug);
    return c ? formatDisplayTitle(c.name || slug) : formatDisplayTitle(String(slug || '').replace(/-/g, ' '));
  };

  const resultsParams = () => ({
    recipient: recipient || 'any',
    category: category || 'any',
    group: group || 'any',
    budget: budget || 'any',
  });

  const applyRecipientPreset = (recipientId) => {
    const rec = resolveRecipient(config, recipientId);
    const extra = { recipient: recipientId || 'any' };
    if (rec?.category && rec.category !== 'any') extra.category = rec.category;
    if (rec?.group && rec.group !== 'any') extra.group = rec.group;
    else extra.group = 'any';
    return extra;
  };

  const onNext = () => {
    if (step === 'recipient') {
      goStep('collection', applyRecipientPreset(recipient));
    } else if (step === 'collection') {
      if (showGroupStep) goStep('group', { category });
      else if (budgetStepEnabled) goStep('budget', { category, group: 'any' });
      else goStep('results', { ...resultsParams(), group: 'any', budget: 'any' });
    } else if (step === 'group') {
      if (budgetStepEnabled) goStep('budget', { group });
      else goStep('results', { ...resultsParams() });
    } else if (step === 'budget') {
      goStep('results', resultsParams());
    }
  };

  const onBack = () => {
    if (step === 'collection') {
      if (recipientStepEnabled) goStep('recipient');
      else navigate('/shop');
    } else if (step === 'group') goStep('collection');
    else if (step === 'budget') {
      if (showGroupStep) goStep('group');
      else goStep('collection');
    } else if (step === 'results') {
      if (budgetStepEnabled) goStep('budget');
      else if (showGroupStep) goStep('group');
      else if (recipientStepEnabled) goStep('recipient');
      else goStep('collection');
    } else if (step === 'recipient') navigate('/shop');
    else navigate('/shop');
  };

  const onSkipGroup = () => {
    if (budgetStepEnabled) goStep('budget', { group: 'any' });
    else goStep('results', { ...resultsParams(), group: 'any', budget: 'any' });
  };
  const onSkipBudget = () => goStep('results', { ...resultsParams(), budget: 'any' });

  const isLastWizardStep =
    step === 'budget' ||
    (step === 'group' && !budgetStepEnabled) ||
    (step === 'collection' && !showGroupStep && !budgetStepEnabled);

  const groupStepTitle = config.group_step_title || 'Which style or tag?';
  const usesTagStep =
    config.use_product_tags !== false &&
    (config.use_admin_gift_groups === false ||
      groupOptions.some((o) => o.id !== 'any' && o.icon === '🏷️'));

  return (
    <div className="store-page gift-finder-page">
      <Helmet>
        <title>{config.seo_title}</title>
        <meta name="description" content={config.seo_description} />
        <link rel="canonical" href="https://salonidesigns.com/gift-finder" />
      </Helmet>

      <div className="container store-page__container py-4 py-lg-5">
        <div className="gift-finder-intro text-center mb-4">
          <p className="gift-finder-eyebrow small text-uppercase mb-2">{config.eyebrow}</p>
          <h1 className="store-page__title mb-2">{config.title}</h1>
          <p className="text-muted mb-0 mx-auto" style={{ maxWidth: '36rem' }}>
            {config.subtitle}
          </p>
        </div>

        {step !== 'results' && !catalogEmpty ? (
          <div className="gift-finder-progress mb-4" aria-hidden="true">
            <div className="gift-finder-progress__track">
              <div className="gift-finder-progress__fill" style={{ width: `${progressPct}%` }} />
            </div>
            <p className="small text-muted text-center mt-2 mb-0">
              Step {stepIndex + 1} of {wizardSteps.length}
            </p>
          </div>
        ) : null}

        {error ? (
          <div className="alert alert-warning" role="alert">
            {error}
          </div>
        ) : null}

        {catalogEmpty ? (
          <div className="text-center py-5">
            <p className="text-muted mb-3">Add products in Admin to use the gift finder.</p>
            <Link to="/shop" className="btn btn-primary rounded-pill">
              Browse shop
            </Link>
          </div>
        ) : null}

        {!catalogEmpty && step === 'recipient' && recipientStepEnabled ? (
          <section className="gift-finder-step" aria-labelledby="gf-recipient-heading">
            <h2 id="gf-recipient-heading" className="h4 text-center mb-3">
              {config.recipient_step_title}
            </h2>
            <p className="text-muted small text-center mb-3">{config.recipient_step_lead}</p>
            <div className="gift-finder-options gift-finder-options--compact">
              {recipientOptions.map((opt) => (
                <OptionCard
                  key={opt.id}
                  option={opt}
                  selected={recipient === opt.id}
                  onSelect={(id) => patchParams({ recipient: id })}
                />
              ))}
            </div>
          </section>
        ) : null}

        {!catalogEmpty && step === 'collection' ? (
          <section className="gift-finder-step" aria-labelledby="gf-collection-heading">
            <h2 id="gf-collection-heading" className="h4 text-center mb-3">
              {config.collection_step_title}
            </h2>
            <p className="text-muted small text-center mb-3">{config.collection_step_lead}</p>
            {loading ? (
              <p className="text-center text-muted">Loading collections…</p>
            ) : (
              <div className="gift-finder-options">
                {collectionOptions.map((opt) => (
                  <OptionCard
                    key={opt.id}
                    option={opt}
                    selected={category === opt.id}
                    onSelect={(id) =>
                      patchParams({ category: id, group: 'any' }, { trackStep: 'collection', trackValue: id })
                    }
                  />
                ))}
              </div>
            )}
          </section>
        ) : null}

        {!catalogEmpty && step === 'group' && showGroupStep ? (
          <section className="gift-finder-step" aria-labelledby="gf-group-heading">
            <h2 id="gf-group-heading" className="h4 text-center mb-3">
              {groupStepTitle}
            </h2>
            <p className="text-muted small text-center mb-3">
              {usesTagStep
                ? config.tag_step_lead ||
                  'Choose a style tag — set tags on each product in Admin → Products.'
                : category === 'any'
                  ? 'Product types and subcategories from your catalog across all collections.'
                  : `Types and subcategories inside ${categoryName(category)}.`}
            </p>
            {groupOptions.length <= 2 && taxonomyGroupCount > 1 ? (
              <p className="text-muted small text-center mb-3">
                You have {taxonomyGroupCount} groups in Admin, but only{' '}
                {groupOptions.length - 1} {groupOptions.length === 2 ? 'has' : 'have'} products assigned. Set each
                product&apos;s <strong>Subcategory</strong> in Admin to see more choices here.
              </p>
            ) : null}
            <div className="gift-finder-options gift-finder-options--compact">
              {groupOptions.map((opt) => (
                <OptionCard
                  key={opt.id}
                  option={opt}
                  selected={group === opt.id}
                  onSelect={(id) => patchParams({ group: id }, { trackStep: 'group', trackValue: id })}
                />
              ))}
            </div>
          </section>
        ) : null}

        {!catalogEmpty && step === 'budget' && budgetStepEnabled ? (
          <section className="gift-finder-step" aria-labelledby="gf-budget-heading">
            <h2 id="gf-budget-heading" className="h4 text-center mb-3">
              {config.budget_step_title}
            </h2>
            <div className="gift-finder-options gift-finder-options--compact">
              {GIFT_BUDGET_OPTIONS.map((opt) => (
                <OptionCard
                  key={opt.id}
                  option={opt}
                  selected={budget === opt.id}
                  onSelect={(id) => patchParams({ budget: id }, { trackStep: 'budget', trackValue: id })}
                />
              ))}
            </div>
          </section>
        ) : null}

        {!catalogEmpty && step === 'results' ? (
          <section className="gift-finder-results" aria-labelledby="gf-results-heading">
            <div className="gift-finder-results__head text-center mb-4">
              <h2 id="gf-results-heading" className="h4 mb-2">
                {config.results_title}
              </h2>
              <p className="text-muted small mb-3">
                {giftFinderSummary({ recipient, category, group, budget }, optionSets)}
              </p>
              {relaxedNotice ? (
                <p className="small text-info mb-3">Showing more options — relaxed filter: {relaxedNotice}.</p>
              ) : null}
              <div className="d-flex flex-wrap justify-content-center gap-2">
                <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill" onClick={onBack}>
                  Change answers
                </button>
                <Link to="/shop" className="btn btn-sm btn-outline-secondary rounded-pill">
                  Browse all
                </Link>
              </div>
            </div>

            {showCustomJewelryPromo ? (
              <aside className="gift-finder-bespoke-promo mb-4" aria-labelledby="gf-bespoke-promo-heading">
                <div className="gift-finder-bespoke-promo__body">
                  <p className="gift-finder-bespoke-promo__eyebrow small text-uppercase mb-1">Custom design</p>
                  <h3 id="gf-bespoke-promo-heading" className="h5 mb-2">
                    {customJewelry.gift_finder_promo_title}
                  </h3>
                  <p className="text-muted small mb-0">{customJewelry.gift_finder_promo_lead}</p>
                </div>
                <Link to="/custom-jewelry" className="btn btn-outline-primary rounded-pill gift-finder-bespoke-promo__cta">
                  {customJewelry.gift_finder_promo_cta}
                </Link>
              </aside>
            ) : null}

            <ProductListingToolbar
              sort={sort}
              onSortChange={setSort}
              inStockOnly={inStockOnly}
              onInStockOnlyChange={setInStockOnly}
              count={results.length}
              className="mb-3"
            />

            {loading ? (
              <CatalogGridSkeleton count={6} />
            ) : results.length === 0 ? (
              <div className="text-center py-5">
                <p className="mb-3">{config.results_empty_message}</p>
                <button
                  type="button"
                  className="btn btn-primary rounded-pill"
                  onClick={() =>
                    goStep(recipientStepEnabled ? 'recipient' : 'collection', {
                      recipient: 'any',
                      category: 'any',
                      group: 'any',
                      budget: 'any',
                    })
                  }
                >
                  Start over
                </button>
              </div>
            ) : (
              <>
                <p className="small text-muted mb-3">
                  {results.length} design{results.length === 1 ? '' : 's'} — showing {(page - 1) * PAGE_SIZE + 1}–
                  {Math.min(page * PAGE_SIZE, results.length)}
                </p>
                <div className="product-grid">
                  {paginated.map((p) => (
                    <ProductCard
                      key={`${p.category}-${p.id}`}
                      product={p}
                      categorySlug={p.category}
                      subtitle={categoryName(p.category)}
                      onBeforeNavigate={() => trackGiftFinderProductClick(p.id, analyticsFilters)}
                    />
                  ))}
                </div>
                {totalPages > 1 ? (
                  <CatalogPagination page={page} totalPages={totalPages} onPageChange={setPage} />
                ) : null}
              </>
            )}
          </section>
        ) : null}

        {!catalogEmpty && step !== 'results' ? (
          <div className="gift-finder-nav d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4 pt-3 border-top">
            <button type="button" className="btn btn-outline-secondary rounded-pill" onClick={onBack}>
              {step === 'collection' || step === 'recipient' ? 'Back to shop' : 'Back'}
            </button>
            <div className="d-flex flex-wrap gap-2 ms-auto">
              {step === 'group' ? (
                <button type="button" className="btn btn-link" onClick={onSkipGroup}>
                  All types
                </button>
              ) : null}
              {step === 'budget' && budgetStepEnabled ? (
                <button type="button" className="btn btn-link" onClick={onSkipBudget}>
                  Any budget
                </button>
              ) : null}
              <button type="button" className="btn btn-primary rounded-pill px-4" onClick={onNext}>
                {isLastWizardStep ? 'See results' : 'Next'}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
